import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * ThemeContext (ST-00127)
 * ----------------------------------------------------------------------------
 * Owns the app's light/dark appearance.
 *
 *   mode          'detect' | 'light' | 'dark'  — what the user chose
 *   resolvedTheme 'light'  | 'dark'            — what is actually painted
 *   setMode(next)                              — persist + apply immediately
 *
 * `detect` follows the OS via matchMedia('(prefers-color-scheme: dark)') and
 * keeps following it (a change listener stays attached while in detect mode).
 *
 * The choice is per device/browser — it lives in localStorage, not the tenant
 * database — so a shared account can look different on a warehouse tablet and
 * on an office desktop.
 *
 * The same storage key is read by the pre-paint inline script in index.html,
 * which sets data-theme on <html> before first paint so there is no white
 * flash on a dark-themed reload. Keep the two in sync.
 */

export const THEME_STORAGE_KEY = 'lexibridge_theme_mode';

export type ThemeMode = 'detect' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: (next: ThemeMode) => void;
}

const MODES: readonly ThemeMode[] = ['detect', 'light', 'dark'];
const DARK_QUERY = '(prefers-color-scheme: dark)';

// Browser chrome colour: Android address bar, Safari tab bar, and the title bar
// of an installed (desktop) PWA, which follows this meta live.
export const META_THEME_COLOR: Readonly<Record<ResolvedTheme, string>> = {
  light: '#45B7AA',
  dark: '#0d1117',
};

/**
 * Dev amber (ST-00112, ST-00138). The Dev build ships its theme-color meta in
 * this colour so the installed Dev app is told apart from Prod at a glance, and
 * it must stay amber in BOTH themes.
 *
 * "Is this Dev?" is decided in exactly one place: scripts/swap-dev-branding.js
 * (npm postbuild, REACT_APP_ENV / REACT_APP_API_URL), which rewrites
 * build/index.html's theme-color to this value only on a Dev build. Nothing
 * here ever writes the amber, so a theme-color meta holding it can only have
 * come from that swap: the runtime reads that marker instead of keeping a
 * second definition of Dev. On a Prod build (and under `vite` dev, where
 * index.html is not swapped) the meta starts teal and follows the theme as
 * before.
 *
 * Title-bar text: browsers pick the text colour from the theme colour itself,
 * not from the page theme, so it is the same in light and dark. #E8963A has a
 * relative luminance of ~0.39, the same band as the teal (~0.38), so browsers
 * draw dark title text on it: ~8.9:1 against black (white would be ~2.4:1).
 */
export const DEV_THEME_COLOR = '#E8963A';

/** True for the Dev build's shipped theme-color (see DEV_THEME_COLOR). */
export const isDevThemeColor = (content: string | null | undefined): boolean =>
  (content ?? '').trim().toUpperCase() === DEV_THEME_COLOR;

const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && (MODES as readonly string[]).includes(value);

const readStoredMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'detect';
  } catch {
    // Private mode / storage disabled — fall back to following the OS.
    return 'detect';
  }
};

const prefersDark = (): boolean => {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
};

const resolve = (mode: ThemeMode): ResolvedTheme =>
  mode === 'detect' ? (prefersDark() ? 'dark' : 'light') : mode;

const applyTheme = (theme: ResolvedTheme): void => {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  // Dev build: keep the shipped amber whatever the theme (ST-00138).
  if (isDevThemeColor(meta.getAttribute('content'))) return;
  meta.setAttribute('content', META_THEME_COLOR[theme]);
};

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'detect',
  resolvedTheme: 'light',
  setMode: () => {},
});

export const ThemeProvider = ({ children }: { children?: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolve(readStoredMode())
  );

  // Write the DOM attribute EAGERLY — before React re-renders — rather than in
  // an effect. useThemeTokens() reads getComputedStyle during render, and
  // effects run after the render phase, so an effect-only apply would hand
  // charts the previous theme's colors for one frame.
  const applyResolved = useCallback((theme: ResolvedTheme) => {
    applyTheme(theme);
    setResolvedTheme(theme);
  }, []);

  // Safety net: covers the first mount if the pre-paint inline script in
  // index.html did not run (blocked script, private mode throw).
  useEffect(() => {
    if (document.documentElement.dataset.theme !== resolvedTheme) {
      applyTheme(resolvedTheme);
    }
  }, [resolvedTheme]);

  // Follow the OS while — and only while — the user is on 'detect'.
  useEffect(() => {
    if (mode !== 'detect') {
      applyResolved(mode);
      return undefined;
    }

    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(DARK_QUERY);
    } catch {
      applyResolved('light');
      return undefined;
    }

    const sync = (event: MediaQueryListEvent) => applyResolved(event.matches ? 'dark' : 'light');
    applyResolved(mql.matches ? 'dark' : 'light');

    // Safari < 14 only supports the deprecated addListener API.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', sync);
      return () => mql.removeEventListener('change', sync);
    }
    mql.addListener(sync);
    return () => mql.removeListener(sync);
  }, [mode, applyResolved]);

  const setMode = useCallback(
    (next: ThemeMode) => {
      const safe: ThemeMode = isThemeMode(next) ? next : 'detect';
      setModeState(safe);
      // Flip in the same tick as the click so there is no frame of the old theme
      // while React works through the mode effect below.
      applyResolved(resolve(safe));
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, safe);
      } catch {
        // Storage unavailable — the choice still applies for this session.
      }
    },
    [applyResolved]
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme, setMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => useContext(ThemeContext);

export default ThemeContext;
